// angular
import { effect, inject, Injectable, PLATFORM_ID } from '@angular/core';
import { Params } from '@angular/router';
import { Socket } from 'ngx-socket-io';
import { isPlatformBrowser } from '@angular/common';
// services
import { StateManagementService } from './state-management.service';
import { StorageService } from '@app/shared/services/storage.service';
import { SocketFactory } from '@app/shared/services/socket-factory.service';
// interfaces & types
import {
  IMessage,
  IMessagesResponse,
  INewMessage,
  IUpdateMessage,
} from '../interfaces/message.interface';
import { SocketEventEnum } from '../enums/messaging-topic.enum';

@Injectable()
export class MessagingService {
  private _messageSocket!: Socket;

  // current lang
  private _currentChatLang: string = 'en';

  // messages & chat id properties
  private _allMessages!: IMessage[];
  private _chatId!: string;

  // dependencies
  private readonly _stateManagementService = inject(StateManagementService);
  private readonly _storageService = inject(StorageService);
  private readonly _socketFactory = inject(SocketFactory);
  private readonly _platformId = inject(PLATFORM_ID);

  constructor() {
    this._connectToMessageSocket();

    // set messages
    effect(() => {
      this._allMessages = this._stateManagementService.allMessages();
    });

    // set language code
    const countryCode = this._storageService.getChatLang();
    this.setChatLang(countryCode);

    // get chat current lang on change
    effect(() => {
      this.setChatLang(this._stateManagementService.currentChatLanguage());
    });
  }

  private _connectToMessageSocket() {
    let chatId = this._storageService.getChatId();

    if (isPlatformBrowser(this._platformId) && chatId) {
      this._messageSocket = this._socketFactory.getSocket('/message');

      this.onMessageReceived();
    }
  }

  // send message api
  public newMessage(newMessage: INewMessage) {
    this._messageSocket.ioSocket.emit(
      SocketEventEnum.createMessage,
      newMessage,
      (data: IMessage) => {
        try {
          const res = data;
        } catch (error) {
          this._stateManagementService.isError.set(true);
          this._stateManagementService.errorMessage.set(error);
        }
      }
    );
  }

  public onMessageReceived() {
    if (this._messageSocket) {
      this._messageSocket.ioSocket.on(
        SocketEventEnum.NEW_MESSAGE,
        (res: IMessage) => {
          const data = res;

          console.log(data);

          console.log(this._allMessages);

          if (this._allMessages?.length < 2) {
            this._stateManagementService.allMessages.set([res]);
          }

          console.log(data);

          console.log(this._allMessages);

          try {
            if (this._allMessages && this._chatId === data.chat_id) {
              // check message is exist or not
              const messageIndex = this._allMessages.findIndex(
                item => !item.is_message_posted
              );

              if (messageIndex !== -1) {
                this._allMessages[messageIndex] = data;
                this._stateManagementService.allMessages.set(this._allMessages);
              } else {
                const lastMessages = this._allMessages?.filter(
                  message => message.is_message_posted
                );

                this._stateManagementService.allMessages.set([
                  ...lastMessages,
                  data,
                ]);
              }
            }
          } catch (error) {
            this._stateManagementService.isError.set(true);
            this._stateManagementService.errorMessage.set(error);
            console.log(error);
          }
        }
      );
    }
  }

  // get all messages api
  public getAllMessages(chatId: string, params?: Params) {
    if (!this._messageSocket) {
      this._connectToMessageSocket();
    }

    this._chatId = chatId;
    // set loading state to true
    this._stateManagementService.isMessagesLoading.set(true);

    this._messageSocket.ioSocket.emit(
      SocketEventEnum.findAllMessages,
      { chat_id: chatId },
      (res: IMessagesResponse) => {
        const data = res;

        try {
          if (data) {
            this._stateManagementService.allMessagesCount.set(data.total);

            if (data?.page > 1) {
              this._stateManagementService.allMessages.set([
                ...data?.data,
                ...this?._allMessages,
              ]);
            } else {
              this._stateManagementService.allMessages.set(data.data);
            }
          }
        } catch (error) {
          this._stateManagementService.isError.set(true);
          this._stateManagementService.errorMessage.set(error);
          this._stateManagementService.isMessagesLoading.set(false);
          console.log(error);
        } finally {
          this._stateManagementService.isMessagesLoading.set(false);
        }
      }
    );
  }

  // get one message by id api
  public getMessageById(messageId: string, is_translate: boolean = false) {
    // this._apollo
    //   .query<OneMessageResponse>({
    //     query: _oneMessageSchema,
    //     variables: { messageId, is_translate },
    //     context: {
    //       headers: {
    //         'x-chat-lang': this._storageService.getChatLang(),
    //       },
    //     },
    //   })
    //   .subscribe({
    //     next: ({ data }) => {
    //       const newData = this._allMessages?.map(message => {
    //         return message._id === data?.getMessage._id
    //           ? data.getMessage
    //           : message;
    //       });
    //
    //       this._stateManagementService.allMessages.set(newData);
    //       this._stateManagementService.oneMessage.set(data.getMessage);
    //     },
    //     error: errors => {
    //       this._stateManagementService.isError.set(true);
    //       this._stateManagementService.errorMessage.set(errors);
    //     },
    //     complete: () => { },
    //   });
  }

  // update message api
  public updateMessage(messageId: string, messageInfo: IUpdateMessage) {
    // this._apollo
    //   .mutate<UpdateMessageResponse>({
    //     mutation: _updateMessageSchema,
    //     variables: { ...messageInfo, messageId },
    //   })
    //   .subscribe({
    //     next: ({ data }) => {
    //       const updatedData = this._allMessages.map<IMessage>(message =>
    //         message._id === messageId
    //           ? { ...message, ...data?.updateMessage }
    //           : message
    //       );
    //
    //       this._stateManagementService.allMessages.set(updatedData);
    //     },
    //     error: errors => {
    //       this._stateManagementService.isError.set(true);
    //       this._stateManagementService.errorMessage.set(errors);
    //     },
    //     complete: () => { },
    //   });
  }

  // remove message api
  public removeMessage(id: string) {
    // this._apollo
    //   .mutate<DeleteMessageResponse>({
    //     mutation: _removeMessageSchema,
    //     variables: { id },
    //   })
    //   .subscribe({
    //     next: ({ data }) => {
    //       if (data?.success) {
    //         const updatedData = this._allMessages.filter(
    //           message => message._id !== id
    //         );
    //
    //         this._stateManagementService.allMessages.set(updatedData);
    //       }
    //     },
    //     error: errors => {
    //       this._stateManagementService.isError.set(true);
    //       this._stateManagementService.errorMessage.set(errors);
    //     },
    //     complete: () => { },
    //   });
  }

  // socket methods

  // new message result in socket
  public getMessageBySocket(messageData: IMessage) {
    // check if chat user is on right chat then receive data
    if (this._allMessages && this._chatId === messageData.chat_id) {
      // check message is exist or not
      const messageIndex = this._allMessages.findIndex(item => {
        return !item.is_message_posted;
      });

      if (messageIndex !== -1) {
        this._allMessages[messageIndex] = messageData;
        this._stateManagementService.allMessages.set(this._allMessages);
      } else {
        this._stateManagementService.allMessages.set([
          ...this._allMessages,
          messageData,
        ]);
      }
    }
  }

  // updated message result in socket
  public updateMessageBySocket(messageData: IMessage) {
    const updatedData = this._allMessages.map<IMessage>(message =>
      message._id === messageData._id ? { ...message, ...messageData } : message
    );

    this._stateManagementService.allMessages.set(updatedData);
  }

  // deleted message result in socket
  public deleteMessageBySocket(messageData: IMessage) {
    const updatedData = this._allMessages.filter(
      message => message._id !== messageData._id
    );

    this._stateManagementService.allMessages.set(updatedData);
  }

  // set current chat lang functionality
  public setChatLang(langCode: string) {
    if (langCode) {
      this._currentChatLang = langCode;
    }
  }
}
